import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { deleteRow, findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import { fail, handle, idOf, nextDocNo, parse, roleCodesOf } from './c07.shared'
import {
  approve,
  createDiscrepancy,
  discRow,
  discrepancies,
  discrepancy,
  escalate,
  found,
  myRechecks,
  recheck,
  reject,
  sweepDiscrepancies
} from './discrepancy.handler'
import { batchOfQr } from './inventoryStock'
import { notify, wmsRoute } from './notification.shared'
import {
  batchNoOf,
  bigQty,
  binByCode,
  binCode,
  containerOf,
  dayDiff,
  defaultWh,
  discsOfSession,
  hasRole,
  iso,
  KADIV_LABEL,
  labelLocation,
  logOf,
  matchedTarget,
  materialCode,
  materialName,
  movement,
  num,
  OPEN_SESSION,
  OWNER_LABEL,
  operatorsOf,
  opnFlow,
  parseWh,
  ROLE,
  reasonName,
  requireWhRole,
  round3,
  SUPERVISOR_LABEL,
  scansOf,
  sessionOf,
  skuCode,
  skuName,
  statusCodeOf,
  TERMINAL_DSR,
  taskOf,
  tasksOf,
  txLogWh,
  unitNameOf,
  up,
  userName,
  type Wh,
  warehouseId,
  whOf,
  wibDate,
  wibStamp,
  withLog
} from './opname.shared'
import { racksOfRm } from './rackScope'
import { slaMinutes } from './slaSetting'

/**
 * Endpoint custom C13 Stock Opname — `/api/v1/wms/opname/*` (WF-OPN-01 + WF-DSR-01):
 * - OPN-01  `sessions`, `start-plan`, `start` (Bagi Area → Mulai Sesi Opname).
 * - OPN-01A `schedule`, `schedule/save`, `schedule/adhoc`.
 * - OPN-01B `session`, `task`, `validate`, `reject-task`, `reassign`, `close`.
 * - OPN-03  `my-tasks`, `task-count`, `identify`, `demo-codes`, `task/start`, `task/lock`, `task/commit` — hitung BUTA:
 *   jumlah sistem tidak pernah dikirim ke operator sebelum `task/lock`. Selama tugas dihitung (gerbang lokasi discan,
 *   belum dikunci) barang di lokasi itu terkunci untuk transaksi lain (`opnameLockRejection`, HTTP 423).
 * - OPN-04/04A `discrepancies`, `discrepancy`, `disc/*` (lihat discrepancy.handler).
 * - OPN-06 `report`.
 */

const DOW = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu']
const DOW_CODE = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
const RM_LEVELS_PALLET = ['Karton', 'Bal']
const actor = (wh: Wh, kind: 'ADMIN' | 'OP' | 'SPV') => [`ACT_OPN_${kind}_${wh}`]

type ScopeKind = 'RACK' | 'PALLET' | 'CONTAINER'
type Area = { code: string; label: string; kind: ScopeKind; zone: string }

/* ============================================================ jadwal & tanggal */

const scheduleOf = (wh: Wh) =>
  readTable('opname_schedule').find(row => idOf(row.warehouse_id) === warehouseId(wh)) as LocalRow | undefined
const pad = (value: number) => String(value).padStart(2, '0')
const ymd = (date: Date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
const dateOf = (text: string) => new Date(`${text}T00:00:00Z`)
const dowOf = (text: string) => dateOf(text).getUTCDay()

/** Tanggal terencana dari awal berlakunya jadwal sampai `ahead` siklus sesudah hari ini (prototype `plannedDates`). */
function plannedDates(schedule: LocalRow | undefined, ahead = 3) {
  if (!schedule) return []
  const today = wibDate()
  const since = String(schedule.effective_from ?? today)
  const out: string[] = []
  if (schedule.frequency === 'WEEKLY') {
    const dow = Math.max(0, DOW_CODE.indexOf(String(schedule.anchor_dow ?? 'TUESDAY')))
    const start = dateOf(since)
    start.setUTCDate(start.getUTCDate() + ((dow - start.getUTCDay() + 7) % 7))
    for (let cursor = start, future = 0; future < ahead && out.length < 260; ) {
      const text = ymd(cursor)
      out.push(text)
      if (text >= today) future += 1
      cursor = new Date(cursor.getTime() + 7 * 86_400_000)
    }
    return out
  }
  const day = Math.min(28, Math.max(1, num(schedule.anchor_day) || 15))
  const first = dateOf(since)
  for (let month = 0, future = 0; future < ahead && month < 60; month += 1) {
    const date = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + month, day))
    const text = ymd(date)
    if (text < since) continue
    out.push(text)
    if (text >= today) future += 1
  }
  return out
}

const sessionsOf = (wh: Wh) =>
  readTable('opname_session')
    .filter(row => idOf(row.warehouse_id) === warehouseId(wh))
    .sort((a, b) => String(b.started_at).localeCompare(String(a.started_at)))
const openSessionOf = (wh: Wh) => sessionsOf(wh).find(row => OPEN_SESSION.includes(statusCodeOf(row)))
const sessionOnDate = (wh: Wh, date: string) => sessionsOf(wh).find(row => row.planned_date === date)

const SESSION_LABEL: Record<string, { label: string; tone: string }> = {
  OPN_SCHEDULED: { label: 'Terjadwal', tone: 'info' },
  OPN_OPEN: { label: 'Open', tone: 'warning' },
  OPN_COUNTING: { label: 'Open', tone: 'warning' },
  OPN_VALIDATION: { label: 'Menunggu Approval', tone: 'warning' },
  OPN_COMPLETED: { label: 'Completed', tone: 'success' },
  OPN_CANCELLED: { label: 'Cancelled', tone: 'neutral' }
}
const modeLabel = (mode: unknown) => (mode === 'CYCLIC' ? 'Cyclic' : 'Full')
const ruleText = (wh: Wh, schedule: LocalRow | undefined) => {
  if (!schedule) return 'Belum ada jadwal.'
  const when =
    schedule.frequency === 'WEEKLY'
      ? `Mingguan · setiap ${DOW[Math.max(0, DOW_CODE.indexOf(String(schedule.anchor_dow)))]}`
      : `Bulanan · setiap tanggal ${num(schedule.anchor_day)}`
  return `${when} · mulai ${String(schedule.start_time)} · target ${num(schedule.target_hours)} jam${wh === 'RM' ? ` · ${modeLabel(schedule.mode)}` : ''}`
}

/* ============================================================ cakupan area */

function rmRacks() {
  return racksOfRm()
    .map(rack => String(rack.code))
    .sort()
}
function cyclicRack(schedule: LocalRow | undefined) {
  const rotation = ((schedule?.cyclic_rotation as string[] | undefined) ?? rmRacks()).filter(Boolean)
  const last = new Map<string, string>()
  sessionsOf('RM')
    .filter(row => row.mode === 'CYCLIC')
    .forEach(row =>
      String(row.scope ?? '')
        .split(',')
        .forEach(code => {
          if (!last.has(code)) last.set(code, String(row.started_at))
        })
    )
  return [...rotation].sort((a, b) => (last.get(a) ?? '').localeCompare(last.get(b) ?? ''))[0] ?? 'R01'
}
function areasOf(wh: Wh, mode: string): Area[] {
  if (wh === 'RM') {
    const racks = mode === 'CYCLIC' ? [cyclicRack(scheduleOf('RM'))] : rmRacks()
    return racks.map(code => ({ code, label: `Rack ${code}`, kind: 'RACK' as const, zone: 'Rak Raw Material' }))
  }
  /* Pallet penyimpanan saja — pallet di area claim / Not Good / staging punya alurnya sendiri. */
  const pallets = readTable('pallet')
    .filter(row => row.pallet_kind === 'FG' && findRow('warehouse_zone', idOf(row.zone_id))?.zone_type === 'STORAGE_FG')
    .map(row => ({
      code: String(row.code),
      label: `Pallet ${String(row.code)}`,
      kind: 'PALLET' as const,
      zone: `Zone ${String(findRow('warehouse_zone', idOf(row.zone_id))?.code ?? String(row.code).split('-')[0])}`
    }))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
  const containers = readTable('container')
    .map(row => ({
      code: String(row.code),
      label: `Container ${String(row.code).replace('CT-', '')}`,
      kind: 'CONTAINER' as const,
      zone: 'Container'
    }))
    .sort((a, b) => a.code.localeCompare(b.code))
  return [...pallets, ...containers]
}

/* ============================================================ OPN-01 */

export function sessions(params: { wh?: string } | undefined) {
  sweepDiscrepancies()
  const wh = params?.wh ? parseWh(params.wh) : defaultWh()
  const schedule = scheduleOf(wh)
  const today = wibDate()
  const open = openSessionOf(wh)
  const canAct = hasRole(wh, 'admin')
  const operators = operatorsOf(wh).map(row => row.label)
  const all = plannedDates(schedule).filter(date => !sessionOnDate(wh, date))
  const past = all.filter(date => date < today)
  const next = all.filter(date => date >= today).slice(0, 1)
  const planned = [...past.slice(-1), ...next].reverse().map(date => ({
    kind: 'PLANNED' as const,
    key: `plan-${date}`,
    planned_date: date,
    weekday: DOW[dowOf(date)],
    start_time: String(schedule?.start_time ?? '08:00'),
    mode: modeLabel(schedule?.mode),
    adhoc: false,
    adhoc_reason: null as string | null,
    status_label:
      date < today
        ? `Terlewat ${dayDiff(`${date}T01:00:00Z`, Date.now())} hari`
        : date === today
          ? 'Jatuh tempo hari ini'
          : 'Terjadwal',
    status_tone: date <= today ? 'danger' : 'info',
    early: date > today,
    summary: `target ${num(schedule?.target_hours)} jam · ${operators.join(', ')}`
  }))
  const adhoc = schedule?.adhoc_date
    ? [
        {
          ...planned[0],
          kind: 'PLANNED' as const,
          key: `adhoc-${String(schedule.adhoc_date)}`,
          planned_date: String(schedule.adhoc_date),
          weekday: DOW[dowOf(String(schedule.adhoc_date))],
          mode: modeLabel(schedule.mode),
          adhoc: true,
          adhoc_reason: (schedule.adhoc_reason as string | null) ?? null,
          status_label:
            String(schedule.adhoc_date) < today
              ? `Terlewat ${dayDiff(`${String(schedule.adhoc_date)}T01:00:00Z`)} hari`
              : String(schedule.adhoc_date) === today
                ? 'Hari ini'
                : 'Terjadwal',
          status_tone: String(schedule.adhoc_date) <= today ? 'danger' : 'info',
          early: String(schedule.adhoc_date) > today,
          start_time: String(schedule.start_time ?? '08:00'),
          summary: `target ${num(schedule.target_hours)} jam · ${operators.join(', ')}`
        }
      ]
    : []
  const rows = sessionsOf(wh).map(row => {
    const code = statusCodeOf(row)
    const tasks = tasksOf(row.id)
    const discs = discsOfSession(row.id)
    const summary = (row.summary as Record<string, number> | null) ?? null
    return {
      kind: 'SESSION' as const,
      key: String(row.id),
      id: String(row.id),
      opn_no: String(row.opn_no),
      mode: modeLabel(row.mode),
      area_count: String(row.scope ?? '')
        .split(',')
        .filter(Boolean).length,
      started_at: wibStamp(String(row.started_at)),
      closed_at: row.closed_at ? wibStamp(String(row.closed_at)) : null,
      tasks_done: tasks.filter(task => task.task_status === 'COMPLETED').length,
      tasks_total: tasks.length,
      open_discs: discs.filter(disc => !TERMINAL_DSR.includes(statusCodeOf(disc))).length,
      status: code,
      status_label: SESSION_LABEL[code]?.label ?? code,
      status_tone: SESSION_LABEL[code]?.tone ?? 'neutral',
      summary: summary ? `${summary.units} unit · ${summary.matched} matched` : null
    }
  })
  return {
    wh,
    owner_label: OWNER_LABEL[wh],
    rule_text: ruleText(wh, schedule),
    can_start: canAct && !open,
    can_act: canAct,
    open_session: open ? { id: String(open.id), opn_no: String(open.opn_no) } : null,
    planned: [...adhoc, ...planned],
    sessions: rows
  }
}

export function startPlan(params: { wh?: string; date?: string; adhoc?: string } | undefined) {
  const wh = parseWh(params?.wh)
  const schedule = scheduleOf(wh)
  const mode = String(schedule?.mode ?? 'FULL')
  const areas = areasOf(wh, mode)
  const operators = operatorsOf(wh)
  const zones = [...new Set(areas.map(area => area.zone))]
  /* Usulan awal dibagi rata per zone (round robin di dalam zone). */
  const proposal = areas.map(area => {
    const index = areas.filter(other => other.zone === area.zone).indexOf(area)
    return { ...area, operator_id: operators.length ? operators[index % operators.length].id : '' }
  })
  const open = openSessionOf(wh)
  return {
    wh,
    mode: modeLabel(mode),
    planned_date: String(params?.date ?? wibDate()),
    adhoc: params?.adhoc === '1',
    target_hours: num(schedule?.target_hours),
    areas: proposal,
    zones: zones.length,
    operators,
    blocked_reason: open
      ? 'Sesi sebelumnya belum Completed.'
      : !areas.length
        ? 'Tidak ada area untuk dihitung di gudang ini.'
        : null
  }
}

export function start(body: {
  wh?: string
  planned_date?: string
  adhoc?: boolean
  areas?: { code: string; operator_id: string }[]
}) {
  const wh = parseWh(body.wh)
  requireWhRole(wh, 'admin', `Mulai opname hanya untuk ${OWNER_LABEL[wh]}.`)
  if (openSessionOf(wh)) fail('Sesi sebelumnya belum Completed.', 'ERR_STATE_CONFLICT', 409)
  const schedule = scheduleOf(wh)
  const mode = String(schedule?.mode ?? 'FULL')
  const known = new Map(areasOf(wh, mode).map(area => [area.code, area]))
  const picked = (body.areas ?? []).filter(row => known.has(row.code))
  if (!picked.length) fail('Tidak ada area untuk dihitung di gudang ini.')
  const operators = new Set(operatorsOf(wh).map(row => row.id))
  if (picked.some(row => !operators.has(row.operator_id))) fail(`Setiap area harus punya petugas Operator WH ${wh}.`)
  const id = newId()
  const { statusId, submissionId } = opnFlow.start(id)
  const opnNo = nextDocNo(`OPN-${wh}`, 'opname_session', 'opn_no')
  const plannedDate = body.planned_date && !body.adhoc ? String(body.planned_date) : null
  const session = insertRow('opname_session', {
    ...auditFieldsForCreate(),
    id,
    opn_no: opnNo,
    warehouse_id: warehouseId(wh),
    mode,
    scope: picked.map(row => row.code).join(','),
    status_id: statusId,
    submission_id: submissionId,
    started_at: iso(),
    closed_at: null,
    planned_date: plannedDate,
    closed_by_id: null,
    submitted_at: null,
    validated_at: null,
    validated_by_id: null,
    target_hours: num(schedule?.target_hours) || (wh === 'RM' ? 6 : 4),
    summary: null,
    log: [
      {
        at: wibStamp(),
        ev: `Sesi dibuka — ${picked.length} tugas hitung dibagikan (${[...new Set(picked.map(row => userName(row.operator_id)))].join(', ')})`,
        by: currentUserId()
      }
    ]
  })
  opnFlow.run(session, 'ACT_OPN_START_SESSION', actor(wh, 'ADMIN'))
  picked.forEach(row => {
    const area = known.get(row.code) as Area
    insertRow('opname_task', {
      ...auditFieldsForCreate(),
      id: newId(),
      opt_no: nextDocNo('OPT', 'opname_task', 'opt_no'),
      session_id: id,
      scope: area.code,
      scope_kind: area.kind,
      scope_label: area.label,
      warehouse_id: warehouseId(wh),
      operator_id: row.operator_id,
      task_status: 'ASSIGNED',
      validation_status: null,
      started_at: null,
      locked_at: null,
      completed_at: null,
      counters: null,
      reject_reason: null,
      validated_by_id: null,
      validated_at: null,
      empty_confirmed: false
    })
  })
  opnFlow.run(findRow('opname_session', id) as LocalRow, 'ACT_OPN_DISPATCH_TASKS', actor(wh, 'ADMIN'))
  if (body.adhoc && schedule)
    updateRow('opname_schedule', String(schedule.id), { adhoc_date: null, adhoc_reason: null })
  txLogWh(wh, 'OPN-01', 'opn-start', opnNo, `Sesi ${opnNo} dimulai — ${picked.length} area, mode ${modeLabel(mode)}`)
  /** Sesi dibuka → tiap petugas hitung mendapat notifikasi jumlah area yang harus dihitung. */
  const perOperator = new Map<string, number>()
  picked.forEach(row => perOperator.set(row.operator_id, (perOperator.get(row.operator_id) ?? 0) + 1))
  perOperator.forEach((areas, operatorId) =>
    notify({
      to: { userIds: [operatorId] },
      type: 'OPNAME_STARTED',
      title: `Stock opname ${opnNo} dimulai`,
      body: `Anda mendapat ${areas} area hitung (${OWNER_LABEL[wh]}) — buka Opname — Operator.`,
      link: wmsRoute('stock-opname/opname-operator'),
      ref: { type: 'opname_session', id }
    })
  )
  return { id, opn_no: opnNo, tasks: picked.length }
}

/* ============================================================ OPN-01A */

export function schedule(params: { wh?: string } | undefined) {
  const wh = parseWh(params?.wh)
  const row = scheduleOf(wh)
  const today = wibDate()
  const dates = plannedDates(row)
  const open = openSessionOf(wh)
  const timeline = dates
    .map(date => {
      const session = sessionOnDate(wh, date)
      if (session) {
        const tasks = tasksOf(session.id)
        const summary = (session.summary as Record<string, number> | null) ?? null
        const matched =
          summary?.matched ??
          tasks.reduce((sum, task) => sum + num((task.counters as Record<string, number> | null)?.matched), 0)
        const adjusted =
          summary?.adjusted ??
          discsOfSession(session.id).filter(disc => statusCodeOf(disc) === 'DSR_ADJUSTED_WRITE_OFF').length
        const done = statusCodeOf(session) === 'OPN_COMPLETED'
        return {
          date,
          weekday: DOW[dowOf(date)],
          state: done ? 'DONE' : 'RUNNING',
          state_label: done ? 'Selesai' : 'Berjalan',
          detail: `${String(session.opn_no)} · ${summary?.locations ?? tasks.length} lokasi · ${matched} cocok · ${adjusted} write-off`
        }
      }
      return {
        date,
        weekday: DOW[dowOf(date)],
        state: date < today ? 'MISSED' : 'PLANNED',
        state_label: date < today ? 'Terlewat' : 'Terjadwal',
        detail:
          date < today
            ? 'Tidak ada sesi opname pada tanggal terencana ini.'
            : `Rencana mulai ${String(row?.start_time ?? '08:00')} · target ${num(row?.target_hours)} jam · ${operatorsOf(
                wh
              )
                .map(op => op.label)
                .join(', ')}`
      }
    })
    .reverse()
  const lastDone = sessionsOf(wh).find(session => statusCodeOf(session) === 'OPN_COMPLETED')
  return {
    wh,
    warehouse_name: String(findRow('mt_warehouses', warehouseId(wh))?.name ?? `WH-${wh}`),
    warehouse_code: `WH-${wh}`,
    schedule: row
      ? {
          frequency: String(row.frequency),
          anchor_day: num(row.anchor_day) || 15,
          anchor_dow: String(row.anchor_dow ?? 'TUESDAY'),
          mode: String(row.mode),
          start_time: String(row.start_time),
          target_hours: num(row.target_hours),
          effective_from: String(row.effective_from),
          adhoc_date: (row.adhoc_date as string | null) ?? null,
          adhoc_reason: (row.adhoc_reason as string | null) ?? null
        }
      : null,
    rule_text: ruleText(wh, row),
    last_done: lastDone ? wibDate(String(lastDone.closed_at ?? lastDone.started_at)) : null,
    next_due: dates.find(date => date >= today && !sessionOnDate(wh, date)) ?? null,
    missed: timeline.filter(item => item.state === 'MISSED').map(item => item.date),
    operators: operatorsOf(wh).map(op => op.label),
    can_edit: hasRole(wh, 'admin'),
    editor_label: OWNER_LABEL[wh],
    running: open ? String(open.opn_no) : null,
    timeline
  }
}

export function saveSchedule(body: {
  wh?: string
  frequency?: string
  anchor_day?: number
  anchor_dow?: string
  mode?: string
  start_time?: string
  target_hours?: number
}) {
  const wh = parseWh(body.wh)
  requireWhRole(wh, 'admin', `Ubah jadwal hanya untuk ${OWNER_LABEL[wh]}.`)
  const row = scheduleOf(wh)
  if (!row) fail('Jadwal gudang ini belum ada.', 'ERR_NOT_FOUND', 404)
  const frequency = body.frequency === 'WEEKLY' ? 'WEEKLY' : 'MONTHLY'
  const day = Math.floor(num(body.anchor_day))
  if (frequency === 'MONTHLY' && !(day >= 1 && day <= 28)) fail('Tanggal pelaksanaan 1–28.')
  if (frequency === 'WEEKLY' && !DOW_CODE.includes(String(body.anchor_dow))) fail('Pilih hari pelaksanaan.')
  if (!/^\d{2}:\d{2}$/.test(String(body.start_time ?? ''))) fail('Jam mulai harus diisi.')
  const hours = num(body.target_hours)
  if (!(hours >= 1 && hours <= 24)) fail('Target durasi 1–24 jam.')
  const mode = wh === 'FG' ? 'FULL' : body.mode === 'CYCLIC' ? 'CYCLIC' : 'FULL'
  updateRow('opname_schedule', String(row?.id), {
    frequency,
    anchor_day: frequency === 'MONTHLY' ? day : null,
    anchor_dow: frequency === 'WEEKLY' ? String(body.anchor_dow) : null,
    mode,
    start_time: String(body.start_time),
    target_hours: hours,
    ...auditFieldsForUpdate()
  })
  txLogWh(wh, 'OPN-01A', 'opn-schedule', `WH-${wh}`, `Jadwal opname diubah — ${ruleText(wh, scheduleOf(wh))}`)
  return { ok: true }
}

export function scheduleAdhoc(body: { wh?: string; date?: string | null; reason?: string }) {
  const wh = parseWh(body.wh)
  requireWhRole(wh, 'admin', `Ubah jadwal hanya untuk ${OWNER_LABEL[wh]}.`)
  const row = scheduleOf(wh) as LocalRow
  if (!body.date) {
    updateRow('opname_schedule', String(row.id), { adhoc_date: null, adhoc_reason: null, ...auditFieldsForUpdate() })
    return { ok: true }
  }
  const reason = String(body.reason ?? '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(body.date))) fail('Pilih tanggal pelaksanaannya.')
  if (String(body.date) < wibDate()) fail('Tanggalnya sudah lewat. Pilih hari ini atau sesudahnya.')
  if (!reason) fail('Alasannya diisi dulu.')
  updateRow('opname_schedule', String(row.id), {
    adhoc_date: String(body.date),
    adhoc_reason: reason,
    ...auditFieldsForUpdate()
  })
  txLogWh(wh, 'OPN-01A', 'opn-adhoc', `WH-${wh}`, `Opname dadakan dijadwalkan ${String(body.date)} — ${reason}`)
  return { ok: true }
}

/* ============================================================ isi lokasi (harapan) */

type Expected = {
  code: string
  unit?: LocalRow
  label?: LocalRow
  name: string
  item_code: string
  unit_name: string
  qty: number
  location: string
}
type PackLine = { pack: LocalRow; name: string; sku_code: string; batch: string; qty: number }

function expectedOf(task: LocalRow): Expected[] {
  const scope = String(task.scope)
  if (task.scope_kind === 'RACK')
    return readTable('rm_unit')
      .filter(unit => unit.unit_status === 'IN_STOCK' && binCode(unit.rack_bin_id).startsWith(`${scope}-`))
      .map(unit => ({
        code: String(unit.qr_code),
        unit,
        name: materialName(unit.material_id),
        item_code: materialCode(unit.material_id),
        unit_name: unitNameOf(unit),
        qty: bigQty(unit),
        location: binCode(unit.rack_bin_id)
      }))
  const kind = task.scope_kind
  const pallet = readTable('pallet').find(row => row.code === scope)
  const container = readTable('container').find(row => row.code === scope)
  return readTable('fg_label')
    .filter(label => label.label_status === 'IN_STOCK')
    .filter(label =>
      kind === 'PALLET'
        ? idOf(label.pallet_id) === String(pallet?.id) && RM_LEVELS_PALLET.includes(String(label.level))
        : idOf(label.container_id) === String(container?.id) && label.level === 'Slop'
    )
    .map(label => ({
      code: String(label.label_no),
      label,
      name: skuName(label.sku_id),
      item_code: skuCode(label.sku_id),
      unit_name: String(label.level),
      qty: 1,
      location: scope
    }))
}
function packsOf(task: LocalRow): PackLine[] {
  if (task.scope_kind !== 'CONTAINER') return []
  const container = readTable('container').find(row => row.code === task.scope)
  return readTable('fg_pack_stock')
    .filter(
      pack => idOf(pack.container_id) === String(container?.id) && pack.status === 'IN_STOCK' && num(pack.qty_pack) > 0
    )
    .map(pack => ({
      pack,
      name: skuName(pack.sku_id),
      sku_code: skuCode(pack.sku_id),
      batch: batchNoOf(pack.batch_id),
      qty: num(pack.qty_pack)
    }))
}
/** Lokasi tujuan yang sah untuk koreksi barang salah tempat. */
function targetLocations(task: LocalRow) {
  if (task.scope_kind !== 'RACK') return [String(task.scope)]
  return readTable('mt_rack_bin')
    .map(bin => String(bin.code))
    .filter(code => code.startsWith(`${String(task.scope)}-`))
    .sort()
}

const AKSI_JEJAK: Record<string, string> = {
  putaway: 'Terakhir putaway',
  'ng-placement': 'Terakhir ditaruh di Karantina NG',
  'pick-commit': 'Terakhir diambil',
  'return-commit': 'Terakhir dikembalikan ke rak',
  'handover-accept': 'Terakhir diserahkan ke produksi',
  'tat-pindah': 'Terakhir dipindah antar rak',
  'fg-putaway': 'Terakhir putaway',
  'misplacement-fix': 'Terakhir dikoreksi lokasinya'
}
function lastTouch(code: string) {
  const row = readTable('tx_log')
    .filter(item => item.doc_ref === code || String(item.detail ?? '').includes(code))
    .sort((a, b) => String(b.ts).localeCompare(String(a.ts)))[0]
  if (!row) return 'tidak ada jejak transaksi'
  return `${AKSI_JEJAK[String(row.action)] ?? `Terakhir ${String(row.action)}`}: ${userName(row.user_id)} · ${wibStamp(String(row.ts))}`
}

/* ============================================================ OPN-03 operator */

const isLocked = (task: LocalRow) => Boolean(task.locked_at)
const isMine = (task: LocalRow) => idOf(task.operator_id) === currentUserId()
const taskWh = (task: LocalRow): Wh => whOf(task.warehouse_id)
const sessionOpen = (task: LocalRow) => ['OPN_OPEN', 'OPN_COUNTING'].includes(statusCodeOf(sessionOf(task.session_id)))

function ownTaskForCount(id: unknown) {
  const task = taskOf(id)
  if (!isMine(task)) fail('Hanya operator pemilik tugas ini yang bisa menghitung.', 'ERR_UNAUTHORIZED_ROLE', 403)
  if (task.task_status === 'COMPLETED') fail('Tugas ini sudah selesai dan terkunci.', 'ERR_STATE_CONFLICT', 409)
  if (!sessionOpen(task)) fail('Sesi opname tugas ini sedang tidak menerima hitungan.', 'ERR_STATE_CONFLICT', 409)
  return task
}

function taskCard(task: LocalRow) {
  const session = sessionOf(task.session_id)
  const status =
    task.task_status === 'COMPLETED'
      ? 'Completed'
      : task.task_status === 'IN_PROGRESS' || task.started_at
        ? 'In Progress'
        : 'Assigned'
  return {
    id: String(task.id),
    opt_no: String(task.opt_no),
    session_id: String(session.id),
    session_no: String(session.opn_no),
    warehouse: taskWh(task),
    scope: String(task.scope),
    scope_kind: String(task.scope_kind),
    scope_label: String(task.scope_label),
    scope_word: task.scope_kind === 'RACK' ? 'Rack' : task.scope_kind === 'PALLET' ? 'Pallet' : 'Container',
    operator_id: String(task.operator_id),
    operator: userName(task.operator_id),
    mine: isMine(task),
    task_status: String(task.task_status),
    status_label: status,
    validation_status: (task.validation_status as string | null) ?? null,
    reject_reason: (task.reject_reason as string | null) ?? null,
    started: Boolean(task.started_at),
    locked: isLocked(task),
    committed: task.task_status === 'COMPLETED'
  }
}

export function myTasks() {
  const roles = roleCodesOf(currentUserId())
  const wh: Wh = roles.some(code => code.endsWith('_fg')) ? 'FG' : 'RM'
  const isOperator = roles.includes(ROLE.operator[wh])
  const tasks = readTable('opname_task')
    .filter(task => taskWh(task) === wh)
    .filter(task => !isOperator || isMine(task))
    .sort((a, b) => {
      const rank = (row: LocalRow) => (row.task_status === 'COMPLETED' ? 1 : 0)
      return rank(a) - rank(b) || String(b.opt_no).localeCompare(String(a.opt_no))
    })
    .map(taskCard)
  return { wh, is_operator: isOperator, tasks, rechecks: isOperator ? myRechecks() : [] }
}

/** Hasil tugas yang sudah dikunci — dibuka SETELAH hitungan fisik dikunci (sebelumnya jumlah sistem tidak dikirim). */
function resultOf(task: LocalRow) {
  const scans = scansOf(task.id)
  const nameOf = (scan: LocalRow) =>
    scan.material_id ? materialName(scan.material_id) : scan.sku_id ? skuName(scan.sku_id) : ''
  const discState = (scan: LocalRow) => {
    const disc = scan.discrepancy_id ? findRow('discrepancy', idOf(scan.discrepancy_id)) : undefined
    return disc
      ? { disc_id: String(disc.id), disc_no: String(disc.dsc_no), disc_status: statusCodeOf(disc) }
      : { disc_id: null, disc_no: null, disc_status: null }
  }
  const row = (scan: LocalRow) => ({
    id: String(scan.id),
    code: String(scan.scanned_code),
    name: nameOf(scan),
    batch: scan.unit_id ? batchOfQr(scan.scanned_code) : '',
    unit_name: String(scan.unit_name ?? ''),
    qty_system: scan.qty_system == null ? null : num(scan.qty_system),
    qty_physical: num(scan.qty_good) + num(scan.qty_ng),
    qty_ng: num(scan.qty_ng),
    ng_reasons: ((scan.ng_reason_ids as string[] | undefined) ?? []).map(reasonName),
    class: String(scan.scan_class),
    from: (scan.from_location as string | null) ?? null,
    decision: (scan.decision as string | null) ?? null,
    to: (scan.to_location as string | null) ?? null,
    trace: (scan.trace as string | null) ?? null,
    ...discState(scan)
  })
  const by = (...classes: string[]) => scans.filter(scan => classes.includes(String(scan.scan_class))).map(row)
  return {
    locked_at: task.locked_at ? wibStamp(String(task.locked_at)) : null,
    sesuai: by('MATCHED', 'PACK_MATCHED'),
    selisih: by('QTY_LESS', 'QTY_MORE', 'PACK_LESS', 'PACK_MORE'),
    salah: by('MISPLACED'),
    hilang: by('MISSING'),
    asing: by('UNKNOWN'),
    targets: targetLocations(task),
    counted: scans.filter(scan => !['MISSING'].includes(String(scan.scan_class))).length
  }
}

export function taskCount(params: { id?: string } | undefined) {
  const task = findRow('opname_task', String(params?.id ?? ''))
  if (!task) return null
  const card = taskCard(task)
  return {
    ...card,
    can_count: card.mine && !card.committed && sessionOpen(task),
    packs: packsOf(task).map(line => ({
      id: String(line.pack.id),
      name: line.name,
      sku_code: line.sku_code,
      batch: line.batch
    })),
    result: isLocked(task) ? resultOf(task) : null
  }
}

const RM_QR = /^[A-Z0-9]+(-[A-Z0-9]+)*:\d{8}:[A-Z0-9]{2}:\d{3,4}$/
const FG_LABEL = /^JLC:[A-Z]:/

/** Kenali kode tanpa membocorkan jumlah sistem (hitung buta). */
export function identify(body: { task_id?: string; code?: string }) {
  const task = taskOf(body.task_id)
  const code = up(body.code)
  const scope = String(task.scope)
  if (code === scope) return { kind: 'SCOPE', code }
  const isLocation =
    readTable('mt_rack').some(row => row.code === code) ||
    readTable('mt_rack_bin').some(row => row.code === code) ||
    readTable('pallet').some(row => row.code === code) ||
    readTable('container').some(row => row.code === code) ||
    readTable('warehouse_zone').some(row => row.code === code)
  if (isLocation) return { kind: 'LOCATION', code }
  if (task.scope_kind === 'RACK') {
    if (FG_LABEL.test(code)) return { kind: 'WRONG_TYPE', code }
    const unit = readTable('rm_unit').find(row => up(row.qr_code) === code)
    if (unit)
      return { kind: 'ITEM', code, known: true, name: materialName(unit.material_id), unit_name: unitNameOf(unit) }
    if (RM_QR.test(code)) return { kind: 'ITEM', code, known: false, name: 'Kode tidak dikenal', unit_name: 'unit' }
    return { kind: 'WRONG_TYPE', code }
  }
  if (RM_QR.test(code)) return { kind: 'WRONG_TYPE', code }
  const label = readTable('fg_label').find(row => up(row.label_no) === code)
  if (label) return { kind: 'ITEM', code, known: true, name: skuName(label.sku_id), unit_name: String(label.level) }
  if (FG_LABEL.test(code)) return { kind: 'ITEM', code, known: false, name: 'Kode tidak dikenal', unit_name: 'label' }
  return { kind: 'WRONG_TYPE', code }
}

/** Kode peraga kamera simulasi (prototype `demoHitung`) — alat demo, bukan daftar harapan untuk operator. */
export function demoCodes(params: { task_id?: string; counted?: string } | undefined) {
  const task = findRow('opname_task', String(params?.task_id ?? ''))
  if (!task) return { next: null, misplaced: null, unknown: null, wrong: null, other_location: null }
  const counted = String(params?.counted ?? '')
    .split(',')
    .filter(Boolean)
  const expected = expectedOf(task)
  const rm = task.scope_kind === 'RACK'
  const next = expected.find(item => !counted.includes(item.code))?.code ?? null
  const scopeCodes = new Set(expected.map(item => item.code))
  const misplaced = rm
    ? readTable('rm_unit').find(
        unit =>
          unit.unit_status === 'IN_STOCK' &&
          unit.rack_bin_id &&
          !scopeCodes.has(String(unit.qr_code)) &&
          !binCode(unit.rack_bin_id).startsWith(`${String(task.scope)}-`)
      )?.qr_code
    : readTable('fg_label').find(
        label =>
          label.label_status === 'IN_STOCK' &&
          !scopeCodes.has(String(label.label_no)) &&
          (task.scope_kind === 'CONTAINER' ? label.level === 'Slop' : RM_LEVELS_PALLET.includes(String(label.level)))
      )?.label_no
  const others = readTable('opname_task').filter(row => taskWh(row) === taskWh(task) && row.scope !== task.scope)
  return {
    next,
    misplaced: misplaced ? String(misplaced) : null,
    unknown: rm ? 'ZZZ-XX99:20260101:01:0001' : 'JLC:C:F:XX99:0000:20260101:000000:B001:9999',
    wrong: rm ? String(readTable('fg_label')[0]?.label_no ?? '') : String(readTable('rm_unit')[0]?.qr_code ?? ''),
    other_location: String(others[0]?.scope ?? (rm ? 'R02' : 'A1-P2'))
  }
}

export function startTask(body: { task_id?: string }) {
  const task = ownTaskForCount(body.task_id)
  if (!task.started_at)
    updateRow('opname_task', String(task.id), {
      started_at: iso(),
      task_status: 'IN_PROGRESS',
      ...auditFieldsForUpdate()
    })
  return { id: task.id, scope: task.scope }
}

type CountLine = { code?: string; good?: number; ng?: number; reason_ids?: string[] }

export function lockTask(body: {
  task_id?: string
  counts?: CountLine[]
  packs?: { pack_id: string; qty: number }[]
  empty_confirmed?: boolean
}) {
  const task = ownTaskForCount(body.task_id)
  if (!task.started_at) fail(`Scan label ${String(task.scope_label)} dulu.`)
  if (isLocked(task)) fail('Hitungan tugas ini sudah dikunci.', 'ERR_STATE_CONFLICT', 409)
  const rm = task.scope_kind === 'RACK'
  const counts = new Map<string, CountLine>()
  ;(body.counts ?? []).forEach(line => {
    if (up(line.code)) counts.set(up(line.code), line)
  })
  counts.forEach(line => {
    if (!rm) return
    if (line.good == null || !(num(line.good) >= 0)) fail('Isi jumlah Good-nya dulu — boleh 0.')
    if (num(line.ng) > 0 && !(line.reason_ids ?? []).length) fail('Barang Not Good harus punya alasan.')
  })
  const packs = packsOf(task)
  const given = new Map((body.packs ?? []).map(row => [String(row.pack_id), row.qty]))
  const missingPacks = packs.filter(
    line => !(given.has(String(line.pack.id)) && num(given.get(String(line.pack.id))) >= 0)
  )
  if (missingPacks.length) fail(`Qty pack belum diisi: ${missingPacks.map(line => line.sku_code).join(', ')}`)
  if (!counts.size && !packs.length && !body.empty_confirmed) fail('Centang konfirmasi dulu.')

  const expected = expectedOf(task)
  const expectedCodes = new Map(expected.map(item => [up(item.code), item]))
  const base = { ...auditFieldsForCreate(), task_id: task.id, session_id: task.session_id }
  const now = iso()
  const insertScan = (row: Record<string, unknown>) =>
    insertRow('opname_scan', {
      ...base,
      id: newId(),
      rack_bin_id: null,
      warehouse_zone_id: null,
      material_id: null,
      sku_id: null,
      unit_id: null,
      label_id: null,
      pack_stock_id: null,
      qty_system: null,
      qty_good: 0,
      qty_ng: 0,
      ng_reason_id: null,
      ng_reason_ids: [],
      unit_name: '',
      scanned_at: now,
      from_location: null,
      decision: null,
      to_location: null,
      trace: null,
      discrepancy_id: null,
      ...row
    })
  counts.forEach((line, code) => {
    const good = rm ? num(line.good) : 1
    const ng = rm ? num(line.ng) : 0
    const reasons = ng > 0 ? (line.reason_ids ?? []) : []
    const hit = expectedCodes.get(code)
    if (hit) {
      const total = round3(good + ng)
      const cls = !rm || total === hit.qty ? 'MATCHED' : total < hit.qty ? 'QTY_LESS' : 'QTY_MORE'
      insertScan({
        scanned_code: hit.code,
        rack_bin_id: hit.unit?.rack_bin_id ?? null,
        warehouse_zone_id: hit.unit?.warehouse_zone_id ?? null,
        material_id: hit.unit?.material_id ?? null,
        sku_id: hit.label?.sku_id ?? null,
        unit_id: hit.unit?.id ?? null,
        label_id: hit.label?.id ?? null,
        qty_system: hit.qty,
        qty_good: good,
        qty_ng: ng,
        ng_reason_id: reasons[0] ?? null,
        ng_reason_ids: reasons,
        unit_name: hit.unit_name,
        scan_class: cls,
        from_location: hit.location,
        trace: cls === 'MATCHED' ? null : lastTouch(hit.code)
      })
      return
    }
    const unit = rm ? readTable('rm_unit').find(row => up(row.qr_code) === code) : undefined
    const label = rm ? undefined : readTable('fg_label').find(row => up(row.label_no) === code)
    const elsewhere = rm
      ? unit && unit.unit_status === 'IN_STOCK' && unit.rack_bin_id
      : label && label.label_status === 'IN_STOCK' && (label.pallet_id || label.container_id)
    if (elsewhere) {
      insertScan({
        scanned_code: rm ? String(unit?.qr_code) : String(label?.label_no),
        material_id: unit?.material_id ?? null,
        sku_id: label?.sku_id ?? null,
        unit_id: unit?.id ?? null,
        label_id: label?.id ?? null,
        qty_system: unit ? bigQty(unit) : 1,
        qty_good: good,
        qty_ng: ng,
        ng_reason_id: reasons[0] ?? null,
        ng_reason_ids: reasons,
        unit_name: unit ? unitNameOf(unit) : String(label?.level ?? ''),
        scan_class: 'MISPLACED',
        from_location: unit ? binCode(unit.rack_bin_id) : labelLocation(label as LocalRow),
        decision: 'UPDATE',
        to_location: targetLocations(task)[0] ?? String(task.scope)
      })
      return
    }
    insertScan({
      scanned_code: code,
      qty_good: good,
      qty_ng: ng,
      unit_name: rm ? 'unit' : 'label',
      scan_class: 'UNKNOWN'
    })
  })
  expected
    .filter(item => !counts.has(up(item.code)))
    .forEach(item =>
      insertScan({
        scanned_code: item.code,
        rack_bin_id: item.unit?.rack_bin_id ?? null,
        warehouse_zone_id: item.unit?.warehouse_zone_id ?? null,
        material_id: item.unit?.material_id ?? null,
        sku_id: item.label?.sku_id ?? null,
        unit_id: item.unit?.id ?? null,
        label_id: item.label?.id ?? null,
        qty_system: item.qty,
        unit_name: item.unit_name,
        scan_class: 'MISSING',
        from_location: item.location,
        trace: lastTouch(item.code)
      })
    )
  packs.forEach(line => {
    const physical = num(given.get(String(line.pack.id)))
    insertScan({
      scanned_code: `Pack ${line.sku_code} · ${line.batch}`,
      sku_id: line.pack.sku_id,
      pack_stock_id: line.pack.id,
      qty_system: line.qty,
      qty_good: physical,
      unit_name: 'Pack',
      scan_class: physical === line.qty ? 'PACK_MATCHED' : physical < line.qty ? 'PACK_LESS' : 'PACK_MORE',
      from_location: String(task.scope)
    })
  })
  updateRow('opname_task', String(task.id), {
    locked_at: now,
    empty_confirmed: Boolean(body.empty_confirmed) && !counts.size,
    ...auditFieldsForUpdate()
  })
  return taskCount({ id: String(task.id) })
}

export function commitTask(body: {
  task_id?: string
  decisions?: { scan_id: string; decision: string; to?: string }[]
}) {
  const task = ownTaskForCount(body.task_id)
  if (!isLocked(task)) fail('Kunci hitungan fisik dulu.', 'ERR_STATE_CONFLICT', 409)
  const wh = taskWh(task)
  const session = sessionOf(task.session_id)
  const scans = scansOf(task.id)
  const targets = targetLocations(task)
  const decisions = new Map((body.decisions ?? []).map(row => [row.scan_id, row]))
  const counters = { matched: 0, misplaced: 0, returned: 0, missing: 0, unknown: 0, qty_diff: 0, pack_diff: 0 }
  const scopeText = `${String(task.scope_label)}`
  scans.forEach(scan => {
    const cls = String(scan.scan_class)
    if (cls === 'MATCHED' || cls === 'PACK_MATCHED') {
      counters.matched += 1
      return
    }
    if (cls === 'MISPLACED') {
      const choice = decisions.get(String(scan.id))
      const decision = choice?.decision === 'RETURN' ? 'RETURN' : 'UPDATE'
      const to =
        decision === 'UPDATE'
          ? choice?.to && targets.includes(choice.to)
            ? choice.to
            : targets[0]
          : String(scan.from_location ?? '')
      updateRow('opname_scan', String(scan.id), { decision, to_location: to })
      if (decision === 'UPDATE') {
        counters.misplaced += 1
        if (scan.unit_id) {
          const bin = binByCode(to)
          const unit = findRow('rm_unit', idOf(scan.unit_id)) as LocalRow
          movement('OPN_RELOCATE', { unit_id: unit.id }, 0, String(task.opt_no), {
            from_rack_bin_id: unit.rack_bin_id,
            to_rack_bin_id: bin?.id ?? null
          })
          updateRow('rm_unit', String(unit.id), { rack_bin_id: bin?.id ?? unit.rack_bin_id, ...auditFieldsForUpdate() })
        } else if (scan.label_id) {
          const label = findRow('fg_label', idOf(scan.label_id)) as LocalRow
          const pallet = readTable('pallet').find(row => row.code === to)
          const container = readTable('container').find(row => row.code === to)
          movement('OPN_RELOCATE', { label_id: label.id }, 0, String(task.opt_no), {
            from_pallet_id: label.pallet_id ?? null,
            to_pallet_id: pallet?.id ?? null,
            from_container_id: label.container_id ?? null,
            to_container_id: container?.id ?? null
          })
          updateRow('fg_label', String(label.id), {
            pallet_id: pallet?.id ?? null,
            container_id: container?.id ?? null,
            zone_id: pallet?.zone_id ?? label.zone_id ?? null,
            ...auditFieldsForUpdate()
          })
        }
        txLogWh(
          wh,
          'OPN-03',
          'misplacement-fix',
          String(scan.scanned_code),
          `Koreksi opname: ${String(scan.from_location)} → ${to}`
        )
      } else {
        counters.returned += 1
        txLogWh(
          wh,
          'OPN-03',
          'misplacement-return',
          String(scan.scanned_code),
          `Barang nyasar di ${String(task.scope)} — instruksi kembalikan ke ${String(scan.from_location)} (lokasi sistem tetap)`
        )
      }
      return
    }
    const unit = scan.unit_id ? (findRow('rm_unit', idOf(scan.unit_id)) as LocalRow) : undefined
    const label = scan.label_id ? (findRow('fg_label', idOf(scan.label_id)) as LocalRow) : undefined
    const physical = num(scan.qty_good) + num(scan.qty_ng)
    const system = num(scan.qty_system)
    const common = {
      wh,
      source: 'OPNAME' as const,
      unit,
      label,
      materialId: (scan.material_id as string | null) ?? null,
      skuId: (scan.sku_id as string | null) ?? null,
      sessionId: String(session.id),
      taskId: String(task.id),
      location: String(scan.from_location ?? task.scope),
      code: String(scan.scanned_code),
      unitName: String(scan.unit_name ?? '')
    }
    let disc: LocalRow | null = null
    if (cls === 'MISSING') {
      counters.missing += 1
      disc = createDiscrepancy({
        ...common,
        type: 'MISSING',
        qty: system,
        trace: String(scan.trace ?? ''),
        event: `Tidak ditemukan saat hitung ${scopeText} (${String(task.opt_no)})`
      })
      txLogWh(
        wh,
        'OPN-03',
        'opn-missing',
        String(scan.scanned_code),
        `Tidak ditemukan di ${String(task.scope)} — ${String(disc.dsc_no)} Investigation`
      )
    } else if (cls === 'QTY_LESS' || cls === 'QTY_MORE') {
      counters.qty_diff += 1
      disc = createDiscrepancy({
        ...common,
        type: cls,
        qty: round3(Math.abs(system - physical)),
        qtySystem: system,
        qtyPhysical: physical,
        trace: `Dihitung ${physical} ${String(scan.unit_name)}, sistem mencatat ${system} ${String(scan.unit_name)} — ${String(scan.trace ?? '')}`,
        event: `Selisih jumlah tumpukan saat hitung ${scopeText} (${String(task.opt_no)}): ${physical} dari ${system} ${String(scan.unit_name)}`
      })
      txLogWh(
        wh,
        'OPN-03',
        'opn-selisih-qty',
        String(scan.scanned_code),
        `Dihitung ${physical}, sistem ${system} ${String(scan.unit_name)} di ${String(task.scope)}`
      )
    } else if (cls === 'PACK_LESS' || cls === 'PACK_MORE') {
      counters.pack_diff += 1
      disc = createDiscrepancy({
        ...common,
        type: cls,
        packId: String(scan.pack_stock_id),
        qty: Math.abs(system - physical),
        qtySystem: system,
        qtyPhysical: physical,
        trace: `Pack agregat — dihitung ${physical}, sistem ${system} Pack`,
        event: `Selisih qty pack di ${String(task.scope)}: ${physical} dari ${system} Pack`
      })
    } else if (cls === 'UNKNOWN') {
      counters.unknown += 1
      disc = createDiscrepancy({
        ...common,
        type: 'UNKNOWN',
        qty: 1,
        trace: 'Kode tidak ada di sistem — investigasi Admin',
        event: `Temuan tak dikenal di ${String(task.scope)} (${String(task.opt_no)})`
      })
      txLogWh(wh, 'OPN-03', 'opn-unknown', String(scan.scanned_code), `Temuan tak dikenal di ${String(task.scope)}`)
    }
    if (disc) updateRow('opname_scan', String(scan.id), { discrepancy_id: disc.id })
  })
  updateRow('opname_task', String(task.id), {
    task_status: 'COMPLETED',
    completed_at: iso(),
    validation_status: 'PENDING',
    counters,
    ...auditFieldsForUpdate()
  })
  txLogWh(
    wh,
    'OPN-03',
    'opn-submit',
    String(task.opt_no),
    `${scopeText}: ${counters.matched} sesuai, ${counters.misplaced} koreksi lokasi, ${counters.returned} perlu dikembalikan, ${counters.qty_diff} selisih jumlah, ${counters.missing} hilang, ${counters.unknown} asing`
  )
  const all = tasksOf(session.id)
  const allDone = all.every(row => row.task_status === 'COMPLETED')
  if (allDone && statusCodeOf(session) === 'OPN_COUNTING') {
    opnFlow.run(session, 'ACT_OPN_FINISH_COUNT', actor(wh, 'OP'))
    updateRow('opname_session', String(session.id), {
      submitted_at: iso(),
      log: withLog(session, 'Seluruh tugas dikunci — sesi menunggu pengesahan Supervisor'),
      ...auditFieldsForUpdate()
    })
  }
  return { id: task.id, counters, session_ready: allDone }
}

/* ============================================================ OPN-01B */

function taskRow(task: LocalRow) {
  const scans = scansOf(task.id).filter(scan => !['MISSING'].includes(String(scan.scan_class)))
  const done = task.task_status === 'COMPLETED'
  const validation = String(task.validation_status ?? '')
  return {
    ...taskCard(task),
    zone:
      task.scope_kind === 'RACK'
        ? `Rack ${String(task.scope)}`
        : task.scope_kind === 'CONTAINER'
          ? 'Container'
          : `Zone ${String(task.scope).split('-')[0]}`,
    progress: `${done ? 1 : 0}/1 area`,
    items: done ? scans.length : null,
    good: done ? scans.reduce((sum, scan) => sum + num(scan.qty_good), 0) : null,
    not_good: done ? scans.reduce((sum, scan) => sum + num(scan.qty_ng), 0) : null,
    validation_label: !done
      ? validation === 'REJECTED'
        ? 'Ditolak'
        : 'Belum selesai dihitung'
      : validation === 'VALIDATED'
        ? 'Disahkan'
        : validation === 'REJECTED'
          ? 'Ditolak'
          : 'Menunggu',
    counters: (task.counters as Record<string, number> | null) ?? null,
    empty_confirmed: Boolean(task.empty_confirmed)
  }
}

function blockersOf(session: LocalRow) {
  const tasks = tasksOf(session.id)
  const discs = discsOfSession(session.id)
  const out: string[] = []
  const notDone = tasks.filter(task => task.task_status !== 'COMPLETED').length
  if (notDone) out.push(`${notDone} tugas hitung belum selesai`)
  if (tasks.some(task => task.task_status === 'COMPLETED' && task.validation_status !== 'VALIDATED'))
    out.push('hasil hitung belum disahkan Supervisor')
  const open = discs.filter(disc => !TERMINAL_DSR.includes(statusCodeOf(disc)))
  if (open.length) {
    const missing = open.filter(
      disc => disc.disc_type === 'MISSING' && statusCodeOf(disc) === 'DSR_INVESTIGATION'
    ).length
    out.push(`${open.length} selisih belum tuntas${missing ? ` (${missing} barang hilang belum diinvestigasi)` : ''}`)
  }
  return out
}

function sessionResultOf(session: LocalRow) {
  const summary = summaryOf(session)
  return {
    locations: summary.locations,
    units: summary.units,
    matched: summary.matched,
    found: summary.found,
    adjusted: summary.adjusted,
    durations: summary.durations.map(entry => ({ operator: userName(entry.operator), minutes: entry.minutes })),
    loss: writeOffRows(readTable('discrepancy').filter(disc => idOf(disc.session_id) === session.id))
  }
}

export function sessionDetail(params: { id?: string } | undefined) {
  sweepDiscrepancies()
  const session = findRow('opname_session', String(params?.id ?? ''))
  if (!session) return null
  const wh = whOf(session.warehouse_id)
  const code = statusCodeOf(session)
  const tasks = tasksOf(session.id).map(taskRow)
  const sum = (key: string) => tasks.reduce((total, task) => total + num(task.counters?.[key]), 0)
  const discs = discsOfSession(session.id).map(discRow)
  const pendingTasks = tasks.filter(task => task.committed && task.validation_status === 'PENDING')
  const ngCount = scansOfSession(session).filter(scan => num(scan.qty_ng) > 0).length
  const blockers = code === 'OPN_COMPLETED' ? [] : blockersOf(session)
  const supervisor = readTable('mt_privileges').find(
    row => findRow('mt_roles', idOf(row.role))?.code === ROLE.supervisor[wh]
  )
  return {
    id: String(session.id),
    opn_no: String(session.opn_no),
    wh,
    mode: modeLabel(session.mode),
    status: code,
    status_label: SESSION_LABEL[code]?.label ?? code,
    status_tone: SESSION_LABEL[code]?.tone ?? 'neutral',
    started_at: wibStamp(String(session.started_at)),
    closed_at: session.closed_at ? wibStamp(String(session.closed_at)) : null,
    area_count: tasks.length,
    counters: {
      matched: sum('matched'),
      corrected: sum('misplaced'),
      returned: sum('returned'),
      selisih: sum('missing') + sum('qty_diff') + sum('pack_diff'),
      unknown: sum('unknown')
    },
    tasks,
    tasks_done: tasks.filter(task => task.committed).length,
    pending_tasks: pendingTasks.map(task => ({
      id: task.id,
      label: task.scope_label,
      operator: task.operator,
      good: task.good,
      not_good: task.not_good
    })),
    ng_pending: wh === 'RM' && !session.validated_at ? ngCount : 0,
    supervisor_label: `${supervisor ? userName(supervisor.user) : ''} (${SUPERVISOR_LABEL[wh]})`,
    owner_label: OWNER_LABEL[wh],
    blockers,
    discrepancies: discs,
    disc_pending: discs.filter(row => row.status === 'DSR_PENDING_APP').length,
    kadiv_label: KADIV_LABEL[wh],
    ng_items: readTable('rm_ng_item')
      .filter(item => item.source === 'OPNAME' && item.source_ref === session.opn_no)
      .map(item => ({
        id: String(item.id),
        ngz_no: String(item.ngz_no ?? ''),
        material_name: materialName(item.material_id),
        batch_no: String(item.batch_no ?? ''),
        sticker_ngq: (item.sticker_ngq as string | null) ?? null,
        qr_code: String(item.qr_code ?? findRow('rm_unit', idOf(item.unit_id))?.qr_code ?? ''),
        qty: num(item.qty),
        unit_name: String(item.unit_name ?? ''),
        reasons: ((item.reason_ids as string[] | undefined) ?? (item.reason_id ? [String(item.reason_id)] : []))
          .map(id => String(findRow('mt_reject_type', id)?.name ?? ''))
          .filter(Boolean),
        status: String(item.item_status)
      })),
    log: logOf(session).map(entry => ({ ...entry, by: userName(entry.by) })),
    // Ringkasan Hasil Sesi (prototype `opn-01b.html` `resultCard`): hanya sesi Completed; angka sama dengan Laporan OPN-06.
    result: code === 'OPN_COMPLETED' ? sessionResultOf(session) : null,
    operators: operatorsOf(wh),
    can: {
      validate: code === 'OPN_VALIDATION' && hasRole(wh, 'supervisor') && pendingTasks.length > 0,
      close: code === 'OPN_VALIDATION' && hasRole(wh, 'admin') && blockers.length === 0,
      is_admin: hasRole(wh, 'admin'),
      is_supervisor: hasRole(wh, 'supervisor'),
      reassign: hasRole(wh, 'admin') && ['OPN_OPEN', 'OPN_COUNTING'].includes(code)
    }
  }
}

const scansOfSession = (session: LocalRow) =>
  readTable('opname_scan').filter(scan => idOf(scan.session_id) === session.id)

export function taskDetail(params: { id?: string } | undefined) {
  const task = findRow('opname_task', String(params?.id ?? ''))
  if (!task) return null
  const session = sessionOf(task.session_id)
  const wh = taskWh(task)
  const row = taskRow(task)
  const scans = isLocked(task)
    ? scansOf(task.id).map(scan => ({
        id: String(scan.id),
        code: String(scan.scanned_code),
        location: String(scan.from_location ?? ''),
        name: scan.material_id
          ? materialName(scan.material_id)
          : scan.sku_id
            ? skuName(scan.sku_id)
            : 'Kode tidak dikenal',
        batch: scan.unit_id ? batchOfQr(scan.scanned_code) : '',
        unit_name: String(scan.unit_name ?? ''),
        qty_system: scan.qty_system == null ? null : num(scan.qty_system),
        good: num(scan.qty_good),
        not_good: num(scan.qty_ng),
        ng_reasons: ((scan.ng_reason_ids as string[] | undefined) ?? []).map(reasonName),
        diff: scan.qty_system == null ? null : round3(num(scan.qty_good) + num(scan.qty_ng) - num(scan.qty_system)),
        class: String(scan.scan_class),
        time: wibStamp(String(scan.scanned_at)).slice(11)
      }))
    : []
  return {
    ...row,
    session_id: String(session.id),
    session_no: String(session.opn_no),
    session_status: statusCodeOf(session),
    wh,
    completed_at: task.completed_at ? wibStamp(String(task.completed_at)) : null,
    validated_by: task.validated_by_id ? userName(task.validated_by_id) : null,
    scans,
    can: {
      validate:
        statusCodeOf(session) === 'OPN_VALIDATION' &&
        hasRole(wh, 'supervisor') &&
        task.task_status === 'COMPLETED' &&
        task.validation_status === 'PENDING'
    }
  }
}

function issueNgItems(session: LocalRow) {
  let n = 0
  scansOfSession(session)
    .filter(scan => num(scan.qty_ng) > 0 && scan.unit_id && scan.scan_class !== 'MISSING')
    .forEach(scan => {
      if (readTable('rm_ng_item').some(item => item.opname_scan_id === scan.id)) return
      const unit = findRow('rm_unit', idOf(scan.unit_id)) as LocalRow
      const reasons = (scan.ng_reason_ids as string[] | undefined) ?? []
      insertRow('rm_ng_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        ngz_no: nextDocNo('NGZ', 'rm_ng_item', 'ngz_no'),
        finding_no: null,
        source: 'OPNAME',
        source_ref: String(session.opn_no),
        opname_scan_id: scan.id,
        unit_id: unit.id,
        material_id: unit.material_id,
        qty: num(scan.qty_ng),
        unit_name: String(scan.unit_name ?? ''),
        reason_id: reasons[0] ?? null,
        reason_ids: reasons,
        item_status: 'AWAITING_LABEL',
        rack_bin_id: unit.rack_bin_id ?? null,
        warehouse_zone_id: unit.warehouse_zone_id ?? null,
        qr_code: unit.qr_code,
        batch_no: batchOfQr(unit.qr_code),
        note: null,
        reported_by: idOf(findRow('opname_task', idOf(scan.task_id))?.operator_id),
        sticker_ngq: null,
        destruction_id: null,
        move_operator_id: null,
        move_task_no: null,
        approved_by: currentUserId(),
        approved_at: iso(),
        log: [
          {
            at: wibStamp(),
            ev: `Terbit dari opname ${String(session.opn_no)} — ${num(scan.qty_ng)} ${String(scan.unit_name)} ${reasons.map(reasonName).join(', ')} (pecahan dari stiker ${String(unit.qr_code)})`,
            by: currentUserId()
          }
        ]
      })
      n += 1
    })
  return n
}

export function validate(body: { task_ids?: string[] }) {
  const ids = [...new Set(body.task_ids ?? [])]
  if (!ids.length) fail('Pilih rak yang akan disahkan.')
  const tasks = ids.map(taskOf)
  const session = sessionOf(tasks[0].session_id)
  const wh = whOf(session.warehouse_id)
  requireWhRole(wh, 'supervisor', `Pengesahan hasil hitung hanya untuk ${SUPERVISOR_LABEL[wh]}.`)
  if (statusCodeOf(session) !== 'OPN_VALIDATION')
    fail('Sesi belum menunggu pengesahan — tunggu semua tugas dikunci.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  if (tasks.some(task => idOf(task.session_id) !== session.id || task.validation_status !== 'PENDING'))
    fail('Sebagian rak sudah diputuskan — muat ulang halamannya.', 'ERR_STATE_CONFLICT', 409)
  tasks.forEach(task =>
    updateRow('opname_task', String(task.id), {
      validation_status: 'VALIDATED',
      validated_by_id: currentUserId(),
      validated_at: iso(),
      ...auditFieldsForUpdate()
    })
  )
  const allValid = tasksOf(session.id).every(task => task.validation_status === 'VALIDATED')
  let ng = 0
  if (allValid) {
    ng = wh === 'RM' ? issueNgItems(session) : 0
    updateRow('opname_session', String(session.id), {
      validated_at: iso(),
      validated_by_id: currentUserId(),
      log: withLog(
        session,
        `Seluruh hasil hitung disahkan${ng ? ` — ${ng} baris Not Good terbit ke antrean Area Not Good` : ''}`
      ),
      ...auditFieldsForUpdate()
    })
  }
  txLogWh(
    wh,
    'OPN-01B',
    'opn-approve',
    String(session.opn_no),
    `${tasks.length} rak disahkan${allValid ? ' — sesi disahkan' : ''}`
  )
  return { validated: tasks.length, session_validated: allValid, ng_items: ng }
}

export function rejectTask(body: { task_id?: string; reason?: string }) {
  const task = taskOf(body.task_id)
  const session = sessionOf(task.session_id)
  const wh = whOf(session.warehouse_id)
  requireWhRole(wh, 'supervisor', `Penolakan hasil hitung hanya untuk ${SUPERVISOR_LABEL[wh]}.`)
  const reason = String(body.reason ?? '').trim()
  if (!reason) fail('Alasannya diisi dulu.')
  if (task.task_status !== 'COMPLETED' || task.validation_status !== 'PENDING')
    fail('Hanya rak yang menunggu pengesahan yang bisa ditolak.', 'ERR_STATE_CONFLICT', 409)
  /* Hasil pindai rak itu dibuang (dan selisih Open hasil hitung itu ditarik) supaya operator benar-benar menghitung ulang. */
  scansOf(task.id).forEach(scan => deleteRow('opname_scan', String(scan.id)))
  readTable('discrepancy')
    .filter(disc => idOf(disc.task_id) === task.id && statusCodeOf(disc) === 'DSR_INVESTIGATION' && !disc.parent_id)
    .forEach(disc => {
      deleteRow('wxl_submission', idOf(disc.submission_id))
      deleteRow('discrepancy', String(disc.id))
    })
  updateRow('opname_task', String(task.id), {
    task_status: 'IN_PROGRESS',
    validation_status: 'REJECTED',
    started_at: null,
    locked_at: null,
    completed_at: null,
    counters: null,
    reject_reason: reason,
    ...auditFieldsForUpdate()
  })
  if (statusCodeOf(session) === 'OPN_VALIDATION') opnFlow.run(session, 'ACT_OPN_RECOUNT', actor(wh, 'SPV'), {}, reason)
  updateRow('opname_session', String(session.id), {
    submitted_at: null,
    log: withLog(
      session,
      `${String(task.scope_label)} ditolak — hitung ulang oleh ${userName(task.operator_id)}: ${reason}`
    ),
    ...auditFieldsForUpdate()
  })
  txLogWh(wh, 'OPN-01B', 'opn-reject-rak', String(task.opt_no), `Rak ditolak — ${reason}`)
  return { id: task.id }
}

export function reassign(body: { task_id?: string; operator_id?: string }) {
  const task = taskOf(body.task_id)
  const wh = taskWh(task)
  requireWhRole(wh, 'admin', `Penugasan ulang hanya untuk ${OWNER_LABEL[wh]}.`)
  if (task.task_status === 'COMPLETED' || isLocked(task))
    fail('Tugas yang sudah dikunci tidak bisa dialihkan.', 'ERR_STATE_CONFLICT', 409)
  const operator = String(body.operator_id ?? '')
  if (!operatorsOf(wh).some(row => row.id === operator)) fail(`Pilih Operator WH ${wh}.`)
  if (operator === idOf(task.operator_id)) fail('Petugasnya tidak berubah.')
  const before = userName(task.operator_id)
  updateRow('opname_task', String(task.id), { operator_id: operator, started_at: null, ...auditFieldsForUpdate() })
  const session = sessionOf(task.session_id)
  updateRow('opname_session', String(session.id), {
    log: withLog(session, `${String(task.opt_no)} dialihkan dari ${before} ke ${userName(operator)}`),
    ...auditFieldsForUpdate()
  })
  txLogWh(wh, 'OPN-01B', 'opn-reassign', String(task.opt_no), `Dialihkan ${before} → ${userName(operator)}`)
  return { id: task.id }
}

/** Baris kerugian sesi (selisih yang di-write-off): dipakai Ringkasan Hasil Sesi OPN-01B dan Laporan OPN-06. */
function writeOffRows(discs: LocalRow[]) {
  return discs
    .filter(disc => statusCodeOf(disc) === 'DSR_ADJUSTED_WRITE_OFF')
    .map(disc => ({
      code: String(disc.scanned_code ?? ''),
      item: disc.material_id ? materialCode(disc.material_id) : skuCode(disc.sku_id),
      qty: `${num(disc.qty)} ${String(disc.unit_name ?? '')}`,
      trace: String(disc.trace ?? ''),
      approval: `Disetujui ${userName(disc.approver_id)}`
    }))
}

function summaryOf(session: LocalRow) {
  const stored = session.summary as Record<string, unknown> | null
  const tasks = tasksOf(session.id)
  const scans = scansOfSession(session)
  if (!scans.length && stored)
    return stored as {
      locations: number
      units: number
      matched: number
      found: number
      adjusted: number
      durations: { operator: string; minutes: number; scope: string }[]
    }
  const discs = readTable('discrepancy').filter(disc => idOf(disc.session_id) === session.id)
  const counters = (key: string) =>
    tasks.reduce((sum, task) => sum + num((task.counters as Record<string, number> | null)?.[key]), 0)
  const byOperator = new Map<string, { minutes: number; scopes: string[] }>()
  tasks.forEach(task => {
    if (!task.started_at || !task.locked_at) return
    const key = idOf(task.operator_id)
    const entry = byOperator.get(key) ?? { minutes: 0, scopes: [] }
    entry.minutes += Math.max(
      1,
      Math.round((Date.parse(String(task.locked_at)) - Date.parse(String(task.started_at))) / 60000)
    )
    entry.scopes.push(String(task.scope_label))
    byOperator.set(key, entry)
  })
  return {
    locations: tasks.filter(task => task.task_status === 'COMPLETED').length,
    units: scans.length,
    matched: counters('matched'),
    found: counters('misplaced') + discs.filter(disc => statusCodeOf(disc) === 'DSR_RESOLVED_FOUND').length,
    adjusted: discs.filter(disc => statusCodeOf(disc) === 'DSR_ADJUSTED_WRITE_OFF').length,
    durations: [...byOperator.entries()].map(([id, entry]) => ({
      operator: id,
      minutes: entry.minutes,
      scope: entry.scopes.join(', ')
    }))
  }
}

export function close(body: { session_id?: string }) {
  const session = sessionOf(body.session_id)
  const wh = whOf(session.warehouse_id)
  requireWhRole(wh, 'admin', `Tutup sesi hanya untuk ${OWNER_LABEL[wh]}.`)
  const blockers = blockersOf(session)
  if (blockers.length) fail(`Sesi belum bisa ditutup — ${blockers.join(' · ')}`, 'ERR_STATE_CONFLICT', 409)
  opnFlow.run(session, 'ACT_OPN_CLOSE', actor(wh, 'ADMIN'))
  const summary = summaryOf(findRow('opname_session', String(session.id)) as LocalRow)
  updateRow('opname_session', String(session.id), {
    closed_at: iso(),
    closed_by_id: currentUserId(),
    summary,
    log: withLog(session, `Sesi ditutup — ${summary.matched} matched, ${summary.adjusted} write-off`),
    ...auditFieldsForUpdate()
  })
  txLogWh(
    wh,
    'OPN-01B',
    'opn-close',
    String(session.opn_no),
    `Sesi ditutup — Matched ${summary.matched}, write-off ${summary.adjusted}`
  )
  return { id: session.id }
}

/* ============================================================ OPN-06 */

const pct = (part: number, whole: number) => (whole ? Math.round((part / whole) * 1000) / 10 : 0)

export function report(params: { wh?: string; from?: string; to?: string; session?: string } | undefined) {
  sweepDiscrepancies()
  const wh = params?.wh ? parseWh(params.wh) : defaultWh()
  const all = sessionsOf(wh)
  const inRange = all.filter(row => {
    const date = wibDate(String(row.started_at))
    return (!params?.from || date >= params.from) && (!params?.to || date <= params.to)
  })
  const options = inRange.map(row => ({
    id: String(row.id),
    label: `${String(row.opn_no)} (${statusCodeOf(row) === 'OPN_COMPLETED' ? 'closed' : 'BERJALAN'})`
  }))
  const picked = inRange.find(row => row.id === params?.session) ?? inRange[0]
  const trend = all
    .filter(row => statusCodeOf(row) === 'OPN_COMPLETED')
    .slice(0, 8)
    .reverse()
    .map(row => {
      const summary = summaryOf(row)
      return { label: String(row.opn_no).replace(/^OPN-(RM|FG)-/, ''), value: pct(summary.matched, summary.units) }
    })
  const base = { wh, total: all.length, shown: inRange.length, options, target: matchedTarget(), trend }
  if (!picked) return { ...base, report: null }
  const summary = summaryOf(picked)
  const closed = statusCodeOf(picked) === 'OPN_COMPLETED'
  const tasks = tasksOf(picked.id)
  const scans = scansOfSession(picked)
  const discs = readTable('discrepancy').filter(disc => idOf(disc.session_id) === picked.id)
  const targetMinutes = num(picked.target_hours) * 60 || slaMinutes('OPNAME_TARGET_MINUTES')
  const totalMinutes = summary.durations.reduce((sum, row) => sum + num(row.minutes), 0)
  return {
    ...base,
    report: {
      id: String(picked.id),
      opn_no: String(picked.opn_no),
      closed,
      mode: modeLabel(picked.mode),
      warehouse_name: String(findRow('mt_warehouses', warehouseId(wh))?.name ?? ''),
      scope_count: String(picked.scope ?? '')
        .split(',')
        .filter(Boolean).length,
      scope_labels: String(picked.scope ?? '')
        .split(',')
        .filter(Boolean)
        .map(code => (wh === 'RM' ? `Rack ${code}` : code))
        .join(', '),
      started_at: wibStamp(String(picked.started_at)),
      closed_at: picked.closed_at ? wibStamp(String(picked.closed_at)) : null,
      responsible: picked.closed_by_id ? `${userName(picked.closed_by_id)} (${OWNER_LABEL[wh]})` : null,
      kpi: {
        locations: summary.locations,
        tasks: tasks.length,
        units: summary.units,
        matched: summary.matched,
        matched_pct: pct(summary.matched, summary.units),
        found: summary.found,
        adjusted: summary.adjusted
      },
      loss: writeOffRows(discs),
      corrections: scans
        .filter(scan => scan.scan_class === 'MISPLACED')
        .map(scan => ({
          code: String(scan.scanned_code),
          from: String(scan.from_location ?? ''),
          to: String(scan.to_location ?? ''),
          decision: scan.decision === 'RETURN' ? 'Dikembalikan ke asalnya' : 'Lokasi dikoreksi'
        })),
      found_rows: discs
        .filter(disc => statusCodeOf(disc) === 'DSR_RESOLVED_FOUND')
        .map(disc => ({
          code: String(disc.scanned_code ?? ''),
          qty: `${num(disc.found_qty ?? disc.qty)} ${String(disc.unit_name ?? '')}`,
          by: userName(disc.resolved_by_id)
        })),
      operators: summary.durations.map(row => ({
        operator: userName(row.operator) || row.operator,
        minutes: num(row.minutes),
        scope: row.scope,
        on_time: !targetMinutes || num(row.minutes) <= targetMinutes
      })),
      scan_rows: scans
        .filter(scan => scan.scan_class !== 'MISSING')
        .map(scan => ({
          operator: userName(findRow('opname_task', idOf(scan.task_id))?.operator_id),
          scope: String(findRow('opname_task', idOf(scan.task_id))?.scope_label ?? ''),
          code: String(scan.scanned_code),
          name: scan.material_id
            ? materialName(scan.material_id)
            : scan.sku_id
              ? skuName(scan.sku_id)
              : 'Kode tidak dikenal',
          good: num(scan.qty_good),
          not_good: num(scan.qty_ng),
          unit_name: String(scan.unit_name ?? '')
        })),
      total_minutes: totalMinutes,
      target_minutes: targetMinutes
    }
  }
}

/** Panel INV-03 "Loss Opname per Sesi": write-off per sesi tertutup (6 terakhir) + rentetan tanpa loss. */
export function lossPanel(wh: Wh) {
  const closed = sessionsOf(wh).filter(row => statusCodeOf(row) === 'OPN_COMPLETED')
  const rows = closed.slice(0, 6).map(row => ({
    label: String(row.opn_no).replace(/^OPN-(RM|FG)-/, ''),
    value: num(summaryOf(row).adjusted)
  }))
  let streak = 0
  for (const row of closed) {
    if (num(summaryOf(row).adjusted)) break
    streak += 1
  }
  return { sessions: closed.length, rows: rows.reverse(), streak }
}

/* ============================================================ kunci lokasi selama dihitung */

/**
 * Selama sebuah tugas sedang dihitung (gerbang lokasi sudah discan, angka fisik belum dikunci), transaksi lain yang
 * menyentuh barang / lokasi itu ditolak 423 — stok di lokasi yang sedang dihitung tidak boleh bergeser.
 */
export function opnameLockRejection(method: string | undefined, url: string | undefined, data: unknown): string | null {
  if (!['post', 'patch', 'put', 'delete'].includes(String(method).toLowerCase())) return null
  const target = String(url ?? '')
  if (target.startsWith('/api/v1/wms/opname/') || target.startsWith('/auth')) return null
  const active = readTable('opname_task').filter(
    task => task.started_at && !task.locked_at && task.task_status === 'IN_PROGRESS'
  )
  if (!active.length) return null
  const body = typeof data === 'string' ? data : JSON.stringify(data ?? '')
  if (!body || body === '""') return null
  const hit = active.find(task => {
    const tokens: string[] = [String(task.scope)]
    if (task.scope_kind === 'RACK') {
      readTable('mt_rack_bin')
        .filter(bin => String(bin.code).startsWith(`${String(task.scope)}-`))
        .forEach(bin => tokens.push(String(bin.code), String(bin.id)))
      expectedOf(task).forEach(item => tokens.push(item.code, String(item.unit?.id ?? '')))
    } else {
      const pallet = readTable('pallet').find(row => row.code === task.scope)
      const container = containerOf(readTable('container').find(row => row.code === task.scope)?.id)
      if (pallet) tokens.push(String(pallet.id))
      if (container) tokens.push(String(container.id))
      expectedOf(task).forEach(item => tokens.push(item.code, String(item.label?.id ?? '')))
    }
    return tokens.filter(Boolean).some(token => body.includes(`"${token}"`))
  })
  return hit
    ? `${String(hit.scope_label)} sedang dihitung (opname ${String(hit.opt_no)}). Transaksi untuk barang di lokasi ini ditunda sampai hitungannya dikunci.`
    : null
}

/* ============================================================ register */

export function registerOpnameHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/opname'
  const get = (path: string, run: (params: Record<string, unknown> | undefined) => unknown) =>
    mock
      .onGet(`${base}/${path}`)
      .reply(config => handle(() => run(config.params as Record<string, unknown> | undefined)))
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  get('sessions', params => sessions(params as { wh?: string }))
  get('start-plan', params => startPlan(params as { wh?: string; date?: string; adhoc?: string }))
  get('schedule', params => schedule(params as { wh?: string }))
  get('session', params => sessionDetail(params as { id?: string }))
  get('task', params => taskDetail(params as { id?: string }))
  get('my-tasks', () => myTasks())
  get('task-count', params => taskCount(params as { id?: string }))
  get('demo-codes', params => demoCodes(params as { task_id?: string; counted?: string }))
  get('discrepancies', params => discrepancies(params as { wh?: string; session?: string; status?: string }))
  get('discrepancy', params => discrepancy(params as { id?: string }))
  get('report', params => report(params as { wh?: string; from?: string; to?: string; session?: string }))
  post('start', start)
  post('schedule/save', saveSchedule)
  post('schedule/adhoc', scheduleAdhoc)
  post('identify', identify)
  post('task/start', startTask)
  post('task/lock', lockTask)
  post('task/commit', commitTask)
  post('validate', validate)
  post('reject-task', rejectTask)
  post('reassign', reassign)
  post('close', close)
  post('disc/escalate', escalate)
  post('disc/recheck', recheck)
  post('disc/approve', approve)
  post('disc/reject', reject)
  post('disc/found', found)
}
